import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";
import {
  AdoptionConnection,
  ConnectionSortBy,
  GetAdoptionConnectionsRequest,
  getAdoptionConnections,
} from "../api/getAdoptionConnections";
import {
  UpdateConnectionAttributionRequest,
  updateConnectionAttribution,
} from "../api/updateConnectionAttribution";

export function useAdoptionConnections(staleOnly = false) {
  const [connections, setConnections] = useState<AdoptionConnection[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "firstConnectedAt", desc: true },
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingConnection, setEditingConnection] =
    useState<AdoptionConnection | null>(null);
  const [savingAttribution, setSavingAttribution] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const createSignal = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  }, [cancelPendingRequest]);

  const requestData = useMemo<GetAdoptionConnectionsRequest>(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      searchTerm: searchTerm || undefined,
      sortBy: sorting.length ? (sorting[0].id as ConnectionSortBy) : undefined,
      sortOrder: sorting.length
        ? sorting[0].desc
          ? "desc"
          : "asc"
        : undefined,
      staleOnly: staleOnly || undefined,
    }),
    [pagination, sorting, searchTerm, staleOnly],
  );

  const refetch = useCallback(() => {
    const signal = createSignal();
    setLoading(true);
    getAdoptionConnections(requestData, signal)
      .then((result) => {
        setConnections(result.connections);
        setTotalCount(result.totalCount);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [createSignal, requestData]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    return () => cancelPendingRequest();
  }, [cancelPendingRequest]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const openAttributionDialog = useCallback(
    (connection: AdoptionConnection) => setEditingConnection(connection),
    [],
  );

  const closeAttributionDialog = useCallback(
    () => setEditingConnection(null),
    [],
  );

  const saveAttribution = useCallback(
    (request: UpdateConnectionAttributionRequest) => {
      if (!editingConnection) {
        return;
      }
      setSavingAttribution(true);
      updateConnectionAttribution(editingConnection.uuid, request)
        .then(() => {
          setEditingConnection(null);
          refetch();
        })
        .finally(() => {
          setSavingAttribution(false);
        });
    },
    [editingConnection, refetch],
  );

  return {
    connections,
    totalCount,
    loading,
    pagination,
    sorting,
    searchTerm,
    handlePaginationChange,
    handleSortingChange,
    handleSearchChange,
    editingConnection,
    savingAttribution,
    openAttributionDialog,
    closeAttributionDialog,
    saveAttribution,
  };
}
